import {
  CheckoutSession,
  CheckoutSessionStatus,
  getClientSecretByCheckoutSessionId,
  LoanStatusExternal,
  PaymentIntent,
  PaymentMethod,
  PaymentMethodType,
  postCreateAccessTokenFromClientSecret,
} from '@nemuru-eng/js-api-client';

export type Customer = NonNullable<CheckoutSession['customer']>;

export class CheckoutSessionEntity {
  private static readonly CHECKOUT_SESSION_STATUS_FINAL = [
    CheckoutSessionStatus.CONFIRMED,
    CheckoutSessionStatus.CANCELLED,
    CheckoutSessionStatus.EXPIRED,
    CheckoutSessionStatus.DENIED,
    CheckoutSessionStatus.DESISTED,
  ];

  protected _checkoutSession: CheckoutSession;
  private clientSecret: string | undefined = undefined;

  constructor(value: CheckoutSession) {
    this._checkoutSession = value;
  }

  public static hasFinalStatus(status: CheckoutSessionStatus): boolean {
    return CheckoutSessionEntity.CHECKOUT_SESSION_STATUS_FINAL.includes(status);
  }

  public hasFinalStatus(): boolean {
    return CheckoutSessionEntity.hasFinalStatus(this._checkoutSession.status);
  }

  public hasCustomerData(): boolean {
    return !!this._checkoutSession.customer?.fullName;
  }

  public hasDistributions(): boolean {
    return !!this._checkoutSession.hpp?.distributions?.length;
  }

  public getPaymentIntent(paymentMethodType: PaymentMethodType): PaymentIntent | null {
    // 1 - check if selected PM corresponds with type
    if (this.selectedPaymentMethodId && this.selectedPaymentMethodType === paymentMethodType) {
      const paymentIntent = this.paymentIntents.find(
        (pi) => pi.paymentMethodType === paymentMethodType && pi.paymentMethodId === this.selectedPaymentMethodId,
      );
      if (paymentIntent) return paymentIntent;
    }

    // 2 - if not, get last updated PI with this type, if not found return null
    const lastUpdated = this.paymentIntents
      .filter((pi) => pi.paymentMethodType === paymentMethodType)
      ?.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));

    return lastUpdated?.[0] ?? null;
  }

  public async getClientSecret(): Promise<string> {
    if (!this.clientSecret) {
      const { clientSecret } = await getClientSecretByCheckoutSessionId({ checkoutSessionId: this.id });
      this.clientSecret = clientSecret;
    }

    return this.clientSecret;
  }

  public async getAccessToken(): Promise<string> {
    const { accessToken } = await postCreateAccessTokenFromClientSecret({
      clientSecret: await this.getClientSecret(),
      checkoutSessionId: this.id,
    });
    return accessToken;
  }

  public get id(): string {
    return this._checkoutSession.id;
  }

  public get status(): CheckoutSessionStatus {
    return this._checkoutSession.status;
  }

  public get currency(): string {
    return this._checkoutSession.totalPrice.currency;
  }

  public get merchantId(): string {
    return this._checkoutSession.merchantId;
  }

  public get agentId(): string {
    return this._checkoutSession.agentId;
  }

  public get hppId(): string | undefined {
    return this._checkoutSession.hpp?.id;
  }

  public get checkoutSession(): CheckoutSession {
    return this._checkoutSession;
  }

  public get customer(): Customer | null {
    return this._checkoutSession.customer;
  }

  public get shipping(): CheckoutSession['shipping'] {
    return this._checkoutSession.shipping;
  }

  public get billing(): CheckoutSession['billing'] | null {
    return this._checkoutSession.billing;
  }

  public get paymentMethods(): PaymentMethod[] {
    return this._checkoutSession.paymentMethods;
  }

  protected get paymentIntents(): PaymentIntent[] {
    return this._checkoutSession.paymentIntents;
  }

  protected get selectedPaymentMethodType(): PaymentMethodType | null {
    return this._checkoutSession.selectedPaymentMethodType;
  }

  protected get selectedPaymentMethodId(): string | null {
    return this._checkoutSession.selectedPaymentMethodId;
  }

  public get paidPaymentMethodType(): PaymentMethodType | undefined {
    return this.paymentIntents.find((pi) => pi.status === LoanStatusExternal.PAID)?.paymentMethodType;
  }
}
